import * as LearningModuleActions from "src/app/store/learning-module/actions";
import * as FromLearningModule from "src/app/store/learning-module/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import * as ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Camera, CameraResultType, CameraSource, Photo } from "@capacitor/camera";
import { combineLatest, Observable } from "rxjs";
import { AppRoutes, CDK_EDITOR_CONFIG } from "src/app/constants";
import { Store } from "@ngrx/store";
import { RootState } from "src/app/store";
import { TranslateService } from "@ngx-translate/core";
import { NavController } from "@ionic/angular";
import { StorageService } from "src/app/services/storage/storage.service";
import { ChapterDto, ChallengeDto, LearningModuleDto, CreateChallengeDto, UpdateChallengeDto } from "@api";
import { first, mergeMap, skipWhile } from "rxjs/operators";

@Component({
  selector: "app-challenge-form",
  templateUrl: "./challenge-form.page.html",
  styleUrls: ["./challenge-form.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChallengeFormPage implements OnInit {
  public readonly routes: typeof AppRoutes = AppRoutes;
  public form: FormGroup;
  public image?: Photo;
  public editor: any = ClassicEditor;
  public breadcrumbs: any[] = [];
  public readonly editorConfig = CDK_EDITOR_CONFIG;
  public readonly loading$: Observable<boolean> = this.store.select(FromLearningModule.selectLoading);
  public readonly moduleId$: Observable<string> = this.store.select(FromRouter.selectModuleId);
  public readonly challengeId$: Observable<string> = this.store.select(FromRouter.selectChallengeId);
  public readonly chapterId$: Observable<string> = this.store.select(FromRouter.selectChapterId);
  public readonly learningModule$: Observable<LearningModuleDto> = this.moduleId$.pipe(
    mergeMap(moduleId => this.store.select(FromLearningModule.selectLearningModule(moduleId))),
  );
  public readonly chapter$: Observable<ChapterDto> = this.chapterId$.pipe(
    mergeMap(chapterId => this.store.select(FromLearningModule.selectChapter(chapterId))),
  );
  public readonly challenge$: Observable<ChallengeDto> = this.challengeId$.pipe(
    mergeMap(challengeId => this.store.select(FromLearningModule.selectChallenge(challengeId))),
  );
  public readonly challenges$: Observable<ChallengeDto[]> = this.chapterId$.pipe(
    mergeMap(chapterId => this.store.select(FromLearningModule.selectChapterChallenges(chapterId))),
  );
  public defaultHref: string;

  constructor(
    private readonly store: Store<RootState>,
    private readonly i18n: TranslateService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly navController: NavController,
    private readonly storage: StorageService,
  ) {}

  public ngOnInit(): void {
    this.form = new FormGroup({
      name: new FormControl(null, [Validators.required]),
      description: new FormControl(null),
      order: new FormControl(1, [Validators.required]),
    });
  }

  public cancel(): void {
    this.navController.back();
  }

  public ckeditorChanged(event: any): void {
    // Needed empty function, otherwise <cdkeditor> does not sync with the form
  }

  public async loadImage(): Promise<void> {
    this.image = await Camera.getPhoto({
      source: CameraSource.Photos,
      resultType: CameraResultType.Base64,
      quality: 100,
    });

    this.form.markAsDirty();
    this.changeDetector.detectChanges();
  }

  public async removeImage(): Promise<void> {
    if (this.image) this.image = null;
    this.challenge$.pipe(first()).subscribe(async challenge => {
      if (challenge.imageUrl) {
        await this.storage.removeFromUrl(challenge.imageUrl);
        this.update({ ...challenge, imageUrl: null }, false);
      }
    });
  }

  public async save(): Promise<void> {
    this.store.dispatch(LearningModuleActions.setLoading());

    let imageUrl: string;
    if (this.image) imageUrl = await this.storage.upload(this.image);

    const payload: CreateChallengeDto = { ...this.form.value, imageUrl };

    this.challengeId$.pipe(first()).subscribe(challengeId => {
      if (challengeId) this.update(payload, true);
      else this.create(payload);
    });
  }

  protected ionViewWillEnter(): void {
    combineLatest([this.learningModule$, this.chapter$, this.challenge$, this.challengeId$])
      .pipe(
        skipWhile(([learningModule, chapter, challenge, challengeId]) => {
          if (challengeId) return !learningModule || !chapter || !challenge;
          return !learningModule || !chapter;
        }),
        first(),
      )
      .subscribe(([learningModule, chapter, challenge, challengeId]) => {
        this.breadcrumbs = [
          { name: this.i18n.instant("DASHBOARD.BREADCRUMB_NAME"), link: `/${AppRoutes.home}` },
          { name: this.i18n.instant("ADMIN_LEARNING_MODULES.BREADCRUMB_NAME"), link: `/${AppRoutes.adminModules}` },
          { name: learningModule.name, link: `/${AppRoutes.adminModules}/${learningModule.id}` },
          {
            name: chapter.name,
            link: `/${AppRoutes.adminModules}/${learningModule.id}/${AppRoutes.chapters}/${chapter.id}`,
          },
        ];
        this.defaultHref = `/${AppRoutes.adminModules}/${learningModule.id}/${AppRoutes.chapters}/${chapter.id}`;

        if (challenge) {
          this.breadcrumbs.push(
            ...[
              {
                name: challenge.name,
                link: `/${AppRoutes.adminModules}/${learningModule.id}/${AppRoutes.chapters}/${chapter.id}/${AppRoutes.challenges}/${challengeId}`,
              },
              {
                name: this.i18n.instant("ADMIN_LEARNING_MODULES.EDIT_CHAPTER"),
                link: `/${AppRoutes.adminModules}/${learningModule.id}/${AppRoutes.chapters}/${chapter.id}/${AppRoutes.challenges}/${challengeId}/${AppRoutes.edit}`,
              },
            ],
          );
          this.form.patchValue({ ...challenge });
        } else {
          this.breadcrumbs.push({
            name: this.i18n.instant("ADMIN_LEARNING_MODULES.ADD_CHALLENGE"),
            link: `/${AppRoutes.adminModules}/${learningModule.id}/${AppRoutes.chapters}/${chapter.id}/${AppRoutes.challenges}/${AppRoutes.add}`,
          });
        }
        this.changeDetector.detectChanges();
      });

    combineLatest([this.challenges$, this.challengeId$])
      .pipe(
        skipWhile(([challenges]) => !challenges.length),
        first(),
      )
      .subscribe(([challenges, challengeId]) => {
        if (!challengeId) this.form.patchValue({ order: challenges.length + 1 });
      });
  }

  protected ionViewDidEnter(): void {
    combineLatest([this.moduleId$, this.chapterId$, this.challengeId$])
      .pipe(first())
      .subscribe(([learningModuleId, chapterId, challengeId]) => {
        this.store.dispatch(LearningModuleActions.getLearningModule({ learningModuleId }));
        this.store.dispatch(LearningModuleActions.getChapter({ learningModuleId, chapterId }));
        this.store.dispatch(LearningModuleActions.getChapterChallenges({ learningModuleId, chapterId }));

        if (challengeId) {
          this.store.dispatch(LearningModuleActions.getChallenge({ learningModuleId, chapterId, challengeId }));
        }
      });
  }

  private create(createChallengeDto: CreateChallengeDto): void {
    combineLatest([this.moduleId$, this.chapterId$])
      .pipe(first())
      .subscribe(([learningModuleId, chapterId]) => {
        this.store.dispatch(
          LearningModuleActions.createChallenge({
            learningModuleId,
            chapterId,
            createChallengeDto: { ...createChallengeDto, chapterId },
          }),
        );
      });
  }

  private update(updateChallengeDto: UpdateChallengeDto, goBack: boolean): void {
    combineLatest([this.moduleId$, this.chapterId$, this.challengeId$])
      .pipe(first())
      .subscribe(([learningModuleId, chapterId, challengeId]) => {
        this.store.dispatch(
          LearningModuleActions.updateChallenge({ learningModuleId, chapterId, challengeId, updateChallengeDto, goBack }),
        );
      });
  }
}
